import { OrbitTurn } from '../types';
import {
  OrbitActionBuilder,
  OrbitActionButton,
  OrbitActionCandidate,
  OrbitActionLink,
  OrbitActionMode,
  OrbitActionStep
} from './types';

export type OrbitL4ResultTone = 'success' | 'pending' | 'error' | 'warning';

/** What a Layer 4 turn draws, picked by the contract's card table from the top row down. */
export type OrbitL4Card =
  | { kind: 'working'; step: OrbitActionStep }
  | { kind: 'picker'; text: string; candidates: OrbitActionCandidate[]; live: boolean }
  | {
      kind: 'confirm';
      text: string;
      step: OrbitActionStep;
      expiresAt: number | null;
      /** `null`: the v1 card's own Confirm. */
      buttons: OrbitActionButton[] | null;
      builder: OrbitActionBuilder | null;
      /** `modal`: the builder opens by itself when the turn completes while this tab polls it. */
      mode: OrbitActionMode | null;
    }
  | { kind: 'expired'; reason: 'clock' | 'changed' }
  | { kind: 'confirmed'; text: string; step: OrbitActionStep; handedOffId: number | null }
  | { kind: 'result'; tone: OrbitL4ResultTone; text: string; link: OrbitActionLink | null }
  | { kind: 'text'; text: string }
  | { kind: 'outOfScope'; text: string };

const answerText = (turn: OrbitTurn): string => turn.answer.answer ?? '';

const parseExpiry = (expiresAt: string | null): number | null => {
  if (expiresAt === null) {
    return null;
  }
  const parsed = Date.parse(expiresAt);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * `null` hands the turn back to the analytics renderer unchanged. Live buttons appear only where
 * `outcome` and `action.state` agree: every turn of one action is served with that action's LATEST
 * state, so this agreement is what keeps an older turn from offering stale buttons.
 */
export const orbitL4Card = (
  turn: OrbitTurn,
  now: number,
  /** The turn just before this one in the session, when known. */
  previous?: OrbitTurn
): OrbitL4Card | null => {
  const { action, outcome } = turn;
  const step = action?.steps[0];

  if (turn.state === 'queued' || turn.state === 'processing') {
    return step !== undefined && (action?.state === 'claimed' || action?.state === 'executing')
      ? { kind: 'working', step }
      : null;
  }

  if (turn.state !== 'completed') {
    return null;
  }

  const text = answerText(turn);

  // Straight after an open picker, `unsupported` is a nudge about it ("Pick one of the rules
  // first."), not an out-of-scope request.
  if (outcome === 'unsupported') {
    return previous?.action?.state === 'awaiting_selection'
      ? { kind: 'text', text }
      : { kind: 'outOfScope', text };
  }

  if (outcome === 'action_refused') {
    if (action === undefined || step === undefined) {
      return { kind: 'text', text };
    }
    if (action.state === 'failed') {
      return { kind: 'result', tone: 'warning', text, link: null };
    }
    return action.state === 'completed'
      ? { kind: 'result', tone: 'error', text, link: null }
      : { kind: 'text', text };
  }

  if (action === undefined || step === undefined) {
    return null;
  }

  if (outcome === 'clarify') {
    return {
      kind: 'picker',
      text,
      candidates: action.candidates,
      live: action.state === 'awaiting_selection'
    };
  }

  if (outcome === 'action_proposed') {
    if (action.state === 'expired') {
      return { kind: 'expired', reason: 'changed' };
    }
    if (action.state !== 'awaiting_confirmation') {
      return {
        kind: 'confirmed',
        text,
        step,
        handedOffId: action.state === 'handed_off' ? step.result?.id ?? null : null
      };
    }
    const expiresAt = parseExpiry(action.expires_at);
    if (expiresAt !== null && expiresAt <= now) {
      return { kind: 'expired', reason: 'clock' };
    }
    return {
      kind: 'confirm',
      text,
      step,
      expiresAt,
      buttons: action.buttons ?? null,
      builder: action.builder ?? null,
      mode: action.mode ?? null
    };
  }

  if (outcome === 'action_performed' || outcome === 'action_handoff') {
    return {
      kind: 'result',
      tone: 'success',
      text,
      link: step.status === 'succeeded' || step.status === 'handed_off' ? step.link : null
    };
  }

  if (outcome === 'action_pending_approval') {
    return { kind: 'result', tone: 'pending', text, link: null };
  }

  return null;
};
